import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';


const QuestionCard = ({ 
  question,
  onAnswerSelect,
  selectedAnswer,
  showFeedback = false,
  isSubmitted = false
}) => {
  const [hoveredOption, setHoveredOption] = useState(null);

  const getOptionClass = (optionId) => {
    const baseClass = "w-full text-left p-4 rounded-lg border transition-all duration-200 ease-out min-touch-target";
    
    if (showFeedback && isSubmitted) {
      if (optionId === question.correctAnswer) {
        return `${baseClass} bg-success-50 border-success-200 text-success-800`;
      } else if (optionId === selectedAnswer && optionId !== question.correctAnswer) {
        return `${baseClass} bg-error-50 border-error-200 text-error-800`;
      } else {
        return `${baseClass} bg-surface-secondary border-border text-text-secondary`;
      }
    } else if (selectedAnswer === optionId) {
      return `${baseClass} bg-secondary-50 border-secondary-200 text-secondary-800 ring-2 ring-secondary-100`;
    } else if (hoveredOption === optionId) {
      return `${baseClass} bg-surface-secondary border-border-focus text-text-primary hover:shadow-md`;
    } else {
      return `${baseClass} bg-surface border-border text-text-primary hover:bg-surface-secondary hover:border-border-focus`;
    }
  };

  const getOptionIcon = (optionId) => {
    if (showFeedback && isSubmitted) {
      if (optionId === question.correctAnswer) {
        return 'CheckCircle2';
      } else if (optionId === selectedAnswer && optionId !== question.correctAnswer) {
        return 'XCircle';
      } else {
        return 'Circle';
      }
    } else if (selectedAnswer === optionId) {
      return 'CheckCircle2';
    } else {
      return 'Circle';
    }
  };

  const getOptionIconColor = (optionId) => {
    if (showFeedback && isSubmitted) {
      if (optionId === question.correctAnswer) {
        return 'var(--color-success)';
      } else if (optionId === selectedAnswer && optionId !== question.correctAnswer) {
        return 'var(--color-error)';
      } else {
        return 'var(--color-text-muted)';
      }
    } else if (selectedAnswer === optionId) {
      return 'var(--color-secondary)';
    } else {
      return 'var(--color-text-muted)';
    }
  };

  return (
    <div className="bg-surface rounded-xl border border-border p-6 card-shadow">
      {/* Question */}
      <div className="mb-6">
        <h2 className="text-xl font-heading font-semibold text-text-primary mb-4 leading-relaxed">
          {question.question}
        </h2>
        
        {question.context && (
          <div className="bg-primary-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-primary-700 leading-relaxed">
              <strong>Context:</strong> {question.context}
            </p>
          </div>
        )}
      </div>

      {/* Answer Options */}
      <div className="space-y-3">
        {question.options.map((option) => (
          <button
            key={option.id}
            className={getOptionClass(option.id)}
            onClick={() => !isSubmitted && onAnswerSelect(option.id)}
            onMouseEnter={() => setHoveredOption(option.id)}
            onMouseLeave={() => setHoveredOption(null)}
            disabled={isSubmitted}
          >
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0 mt-0.5">
                <Icon 
                  name={getOptionIcon(option.id)}
                  size={20}
                  color={getOptionIconColor(option.id)}
                  strokeWidth={2}
                />
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="font-medium text-sm">
                    Option {option.id.toUpperCase()}
                  </span>
                </div>
                <p className="text-base leading-relaxed">
                  {option.text}
                </p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Feedback Section */}
      {showFeedback && isSubmitted && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className={`rounded-lg p-4 ${
            selectedAnswer === question.correctAnswer 
              ? 'bg-success-50 border border-success-200' :'bg-error-50 border border-error-200'
          }`}>
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <Icon 
                  name={selectedAnswer === question.correctAnswer ? "CheckCircle2" : "XCircle"}
                  size={20}
                  color={selectedAnswer === question.correctAnswer ? "var(--color-success)" : "var(--color-error)"}
                  strokeWidth={2}
                />
              </div>
              <div>
                <h4 className={`font-heading font-semibold mb-2 ${
                  selectedAnswer === question.correctAnswer ? 'text-success-800' : 'text-error-800'
                }`}>
                  {selectedAnswer === question.correctAnswer ? 'Correct!' : 'Incorrect'}
                </h4>
                <p className={`text-sm leading-relaxed ${
                  selectedAnswer === question.correctAnswer ? 'text-success-700' : 'text-error-700'
                }`}>
                  {question.explanation}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;